import React, { FC } from 'react';
import { useFormik } from 'formik';
import { Size as BundleSize } from 'src/components/ProductDetail/ProductSizes';
import { ColorAndSize, Size } from 'src/components/BundleProduct/types';
import classnames from 'classnames';
import {
    MAX_QUANTITY_FOR_PRODUCTS,
    MAX_QUANTITY_FOR_PRODUCTS_WITHOUT_LIMIT,
} from 'src/components/ProductDetail/utils/sizeForm';
import { CheckLabel } from 'src/components/Common/CheckLabel';
import { getSwatchAccessibleName } from 'src/components/Swatch/SwatchOption';

import { BundleButton } from '../BundleProduct/BundleButton';

import classes from './SizesForm.scss';
import { QtyPicker } from './QtyPicker';

type Props = {
    initialValues: Pick<ColorAndSize, 'sizes'>;
    handleSubmit: (values: Size[]) => void;
    sizes: BundleSize[];
    closeModal: () => void;
    colorId: number;
    onSizeChange?: (sizes: Size[]) => void;
};

export const SizesForm: FC<Props> = ({ initialValues, handleSubmit, sizes, closeModal, colorId, onSizeChange }) => {
    const formik = useFormik({
        initialValues,
        onSubmit: (values) => {
            handleSubmit(values.sizes);
            closeModal();
        },
    });

    const getSizeNameById = (sizeId: number) => {
        const size = sizes.find((size) => size.id === sizeId);

        if (size) return size.title;
    };

    const getSizeMaxQtyById = (sizeId: number) => {
        const size = sizes.find((size) => size.id === sizeId);

        if (size) return size.maxQty;
    };

    const handleChange = (quantity: number, sizeId: number) => {
        const maxQty = getSizeMaxQtyById(sizeId);
        const computedMaxQty = maxQty
            ? maxQty >= 10000
                ? MAX_QUANTITY_FOR_PRODUCTS
                : maxQty
            : MAX_QUANTITY_FOR_PRODUCTS_WITHOUT_LIMIT;

        const computedQuantity = quantity > computedMaxQty ? computedMaxQty : quantity;

        const computedSizes = formik.values.sizes.map((size) => {
            if (sizeId !== size.id) return size;
            return { ...size, quantity: computedQuantity };
        });

        formik.setFieldValue('sizes', computedSizes);
        onSizeChange && onSizeChange(computedSizes);
    };

    const handleDecreaseQuantity = (quantity: number, id: number) => {
        if (quantity > 0) {
            handleChange(quantity - 1, id);
        }
    };

    const handleIncreaseQuantity = (quantity: number, id: number, maxQty: number | null) => {
        if (!maxQty || quantity < maxQty) {
            handleChange(quantity + 1, id);
        }
    };

    const checkStock = (sizeId: number) => {
        const size = sizes.find((size) => size.id === sizeId);
        if (size) return size.stock;
    };

    const qtySummary = () => {
        let qty = 0;
        formik.values.sizes.map((size) => {
            qty += size.quantity;
        });

        return qty;
    };

    const onInputClick = (size: Size) => {
        if (size.quantity === 0) {
            handleChange(1, size.id);
        } else {
            handleChange(0, size.id);
        }
    };

    return (
        <form onSubmit={formik.handleSubmit} className={classes.form}>
            <div className={classes.formContainer}>
                {formik.values.sizes.map((size) => {
                    const maxQty = getSizeMaxQtyById(size.id);
                    const msg = maxQty && maxQty < 100 ? `(${maxQty} available)` : '(100+ available)';
                    const inStock = checkStock(size.id) !== 'OutOfStock' && maxQty && maxQty > 0;
                    const sizeName = getSizeNameById(size.id) || '';

                    return (
                        <div key={size.id} className={classes.inputContainer}>
                            <div className={classes.sizeContainer}>
                                <input
                                    className={classes.sizeRadioInput}
                                    id={`sizeRadio-${size.id}`}
                                    type="radio"
                                    checked={size.quantity > 0}
                                    onChange={() => onInputClick(size)}
                                    onClick={() => {
                                        if (size.quantity > 0) {
                                            onInputClick(size);
                                        }
                                    }}
                                    disabled={!inStock}
                                    aria-label={getSwatchAccessibleName('Size', sizeName)}
                                />
                                <CheckLabel htmlFor={`sizeRadio-${size.id}`} checked={size.quantity > 0} />
                                <span
                                    className={classnames(classes.size, {
                                        [classes.longSize]: sizeName.length > 11,
                                    })}
                                >
                                    {sizeName}
                                </span>
                                {inStock ? (
                                    <span className={classes.stockText}>
                                        <img className={classes.tick} src="../../../static/icons/tick.svg" alt="" />
                                        <span className={classnames({ [classes.inStock]: maxQty && maxQty < 100 })}>
                                            In Stock
                                        </span>
                                        <span className={classes.exactStock}>{msg}</span>
                                    </span>
                                ) : (
                                    <span className={classes.outOfStock}>Out of Stock</span>
                                )}
                            </div>

                            <QtyPicker
                                id={size.id}
                                quantity={size.quantity}
                                handleChange={handleChange}
                                onDecreaseQuantity={handleDecreaseQuantity}
                                onIncreaseQuantity={handleIncreaseQuantity}
                                colorId={colorId}
                                disabled={!inStock}
                                maxQty={maxQty}
                            />
                        </div>
                    );
                })}
            </div>
            {qtySummary() !== 0 ? (
                <div className={classes.sizeButtons}>
                    <BundleButton
                        type="submit"
                        size="big"
                        background="orange"
                        icon="check.png"
                        iconOnRight
                        bold
                    >{`Confirm ${qtySummary()} Selections`}</BundleButton>
                </div>
            ) : (
                <div className={classes.sizeButtons}>
                    <BundleButton type="submit" size="medium" background="gray">
                        Cancel
                    </BundleButton>
                </div>
            )}
        </form>
    );
};
